import React, { useState, useRef } from "react";
import { toast } from "react-toastify";
import DateTimePicker from "../components/DateTimePicker";
import { submitJobRequest } from "../services/careerService";

interface FormDataType {
  name: string;
  email: string;
  phone: string;
  interest: string;
  message: string;
  resume: File | null;
}
const CareerForm: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<FormDataType>({
    name: "",
    email: "",
    phone: "",
    interest: "",
    message: "",
    resume: null,
  });

  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const validateForm = () => {
    const newErrors: any = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
    ) {
      newErrors.email = "Invalid email address";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[0-9]{7,12}$/.test(formData.phone)) {
      newErrors.phone = "Enter valid phone number";
    }

    if (!formData.resume) {
      newErrors.resume = "Resume is required";
    } else {
      const allowedExtensions = ["pdf", "doc", "docx"];
      const fileExtension = formData.resume.name
        .split(".")
        .pop()
        ?.toLowerCase();

      if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
        newErrors.resume = "Only PDF, DOC, and DOCX files are allowed";
      }

      const maxSize = 5 * 1024 * 1024; // 5 MB

      if (formData.resume.size > maxSize) {
        newErrors.resume = "Resume size must not exceed 5 MB";
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const payload = new FormData();

      payload.append("name", formData.name);
      payload.append("email", formData.email);
      payload.append("phone", formData.phone);
      payload.append("interest", formData.interest);
      payload.append("message", formData.message);

      if (formData.resume) {
        payload.append("resume", formData.resume);
      }

      const response = await submitJobRequest(payload);

      toast.success("Request submitted successfully!");
      setFormData({
        name: "",
        email: "",
        phone: "",
        interest: "",
        message: "",
        resume: null,
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error: any) {
      console.error(error);

      if (error.status === 409) {
        toast.error(error.message);
      } else toast.success("Error occurred while form submission");
    } finally {
      setLoading(false);
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    const allowedExtensions = ["pdf", "doc", "docx"];
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
      setErrors({
        ...errors,
        resume: "Only PDF, DOC, and DOCX files are allowed.",
      });

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      return;
    }

    setErrors({
      ...errors,
      resume: "",
    });

    const maxSize = 5 * 1024 * 1024; // 5MB

    if (file.size > maxSize) {
      setErrors({
        ...errors,
        resume: "File size should not exceed 5MB.",
      });

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      return;
    }
    setFormData({
      ...formData,
      resume: file,
    });
  };
  return (
    <section className="career-section-form">
      <div className="hero-border">
        <div className="career-wrapper">
          {/* LEFT SIDE */}

          {/* RIGHT SIDE FORM */}

          <div className="reservation-form">
            <form onSubmit={handleSubmit}>
              <div className="form-group" style={{ textAlign: "center" }}>
                <div className="career-title" style={{ textAlign: "center" }}>
                  Take a Seat at the Table
                </div>
                Every great house is shaped by the people within it. If you’re
                passionate about hospitality, food, culture, and creating
                memorable guest experiences, we’d love to meet you. 
                <br />
                <br />
                Tell us a little about yourself below.
              </div>
              <div className="career-form">
                <div className="career-inner-form">
                  <div className="form-group">
                    <input
                      type="text"
                      placeholder="Name"
                      value={formData.name}
                      maxLength={30}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          name: e.target.value,
                        })
                      }
                    />
                    <div className="career-error">
                      {errors.name && <span>{errors.name}</span>}
                    </div>
                  </div>

                  <div className="form-group">
                    <input
                      type="email"
                      placeholder="Email Address"
                      value={formData.email}
                      maxLength={30}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          email: e.target.value,
                        })
                      }
                    />
                    <div className="career-error">
                      {errors.email && <span>{errors.email}</span>}
                    </div>
                  </div>

                  <div className="form-group">
                    <input
                      type="text"
                      placeholder="Phone Number"
                      value={formData.phone}
                      maxLength={12}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          phone: e.target.value,
                        })
                      }
                    />
                    <div className="career-error">
                      {errors.phone && <span>{errors.phone}</span>}
                    </div>
                  </div>
                  <div className="form-group">
                    <select
                      value={formData.interest}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          interest: e.target.value,
                        })
                      }
                      className="career-select"
                    >
                      <option value="">Current Position</option>
                      <option value="Manager">Manager</option>
                      <option value="Server">Server</option>
                      <option value="Runner">Runner</option>
                      <option value="Hostess">Hostess</option>
                      <option value="Chef">Chef</option>
                      <option value="Utility">Utility</option>
                    </select>

                    <div className="career-error">
                      {errors.interest && <span>{errors.interest}</span>}
                    </div>
                  </div>
                  <div className="form-group">
                    <textarea
                      rows={3}
                      placeholder="Cover Letter"
                      style={{ paddingLeft: "20px" }}
                      value={formData.message}
                      maxLength={500}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          message: e.target.value,
                        })
                      }
                    />
                    {errors.message && <span>{errors.message}</span>}
                  </div>
                  {/* <div className="form-group">
                    {" "}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      placeholder="Upload"
                    />
                    <div className="career-error">
                      {errors.resume && <span>{errors.resume}</span>}
                    </div>
                  </div> */}
                  <div className="form-group">
                    <label htmlFor="resume-upload" className="upload-label">
                      {formData.resume ? formData.resume.name : "Upload File"}
                    </label>

                    <input
                      id="resume-upload"
                      type="file"
                      ref={fileInputRef}
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileChange}
                      style={{ display: "none" }}
                    />

                    <div className="career-error">
                      {errors.resume && <span>{errors.resume}</span>}
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="submit-btn"
                    style={{ marginTop: "20px" }}
                  >
                    {loading ? "PLEASE WAIT..." : "APPLY"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CareerForm;
